/**
 * 학생 검사 입장 상태 + next 경로 안전화 유틸.
 *
 * 입장 상태는 서버에 영속하지 않고 학생 FE sessionStorage에만 둔다.
 * 같은 세션(examId)을 한 번 통과하면 재진입 시 코드 재입력을 생략한다.
 *
 * ★ static export(`output:"export"`) 제약: sessionStorage/window 접근은 모듈
 *   top-level 이 아니라 이 함수들 안(호출 시점 = 이벤트 핸들러/effect)에서만 일어난다.
 */

const ENTERED_KEY_PREFIX = "exam:entered:";

/** 입장 성공 기록. 세션(examId) 단위로, 이후 게이트가 이 키로 재입력을 생략한다. */
export const markExamEntered = (examId: number): void => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(`${ENTERED_KEY_PREFIX}${examId}`, "1");
};

/** 이 세션(examId) 을 이미 코드로 통과했는가. */
export const hasEnteredExam = (examId: number): boolean => {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(`${ENTERED_KEY_PREFIX}${examId}`) === "1";
};

/**
 * 입장 기록 삭제. 챕터를 최종 제출한 뒤 호출한다.
 * 같은 챕터를 다시 할당받아도 세션(examId)이 같아서, 지우지 않으면 두 번째 응시가 코드 없이 열린다.
 */
export const clearExamEntered = (examId: number): void => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(`${ENTERED_KEY_PREFIX}${examId}`);
};

/**
 * `next` 쿼리를 내부 경로로만 제한(open redirect 방지).
 *
 * 허용: `/` 로 시작하는 단일 슬래시 내부 경로(쿼리/해시 포함). 예: `/problem`,
 *   `/essay-learning/play?chapterId=12`.
 * 거부(→ `/` 폴백): 외부 절대 URL(`http://`, `https://`, `//host`),
 *   프로토콜 상대(`//`), 백슬래시 우회(`/\`), 문자열이 아니거나 빈 값.
 */
export const sanitizeNextPath = (next: unknown): string => {
  if (typeof next !== "string" || next.length === 0) return "/";
  // 반드시 단일 슬래시로 시작. `//` `/\` 는 프로토콜 상대/우회 → 거부.
  if (next[0] !== "/") return "/";
  if (next[1] === "/" || next[1] === "\\") return "/";
  // 스킴 포함 절대 URL 방어(인코딩 우회 포함).
  if (/^[a-z][a-z0-9+.-]*:/i.test(next)) return "/";
  return next;
};
